import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AssetGenJob, ComposedRevisionViewV1, Creation, CreationFilmMaterialV1,
  CreationFilmRunV1, CreationVersion, WorkshopSelection, WorkshopState,
} from '@djgg/creations';
import { composedNotInYetLineV1, emptyWorkshop, workshopSelectionStatus, workshopSlot } from '@djgg/creations';
import { useServices } from '../services/context.js';
import type { AssetVariant, Callables } from '../services/types.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { CREATION_GUIDES } from '../components/CreationGuide.js';
import { BuildTracker } from '../components/BuildTracker.js';
import { playUrl } from '../services/playUrl.js';
import type { BuildStatusView } from './buildStatus.js';
import { analysisDiscoveries, journeyTiming, mediaEmptyState } from './creationJourney.js';
import { CreationShow } from './CreationShow.js';
import { buildShowChapter, type ShowArtifact } from './creationShowModel.js';
import { appendFilmMaterialArtifacts } from './creationFilmModel.js';
import { useCreationFilm } from './useCreationFilm.js';

export interface WorkshopViewProps {
  onEvent?: (event: import('@djgg/creations').ShowEvent) => void;
  title: string; creationId: string; originVersion: number;
  /** Identity of the watched run; differs from originVersion after a failed revision. */
  showVersion: number; attachedAssets: ReadonlyArray<{ variantId: string }>; view: BuildStatusView | null;
  active: Pick<CreationVersion, 'n' | 'buildStatus' | 'assetsSnapshot' | 'previewUrl' | 'posterUrl'> | null; now: number;
  version?: Pick<CreationVersion, 'buildStatus' | 'buildTiming' | 'buildMode' | 'creationRun'> & Partial<Pick<CreationVersion, 'n'>> | null;
  history?: Array<Pick<CreationVersion, 'buildStatus' | 'buildTiming' | 'buildMode'>>;
  jobs: Array<AssetGenJob & { id: string }>; variants: Array<AssetVariant & { genJobId?: string }>;
  discoveries?: ShowArtifact[]; state: WorkshopState; draft: string; suggestion?: string; busy?: boolean; error?: string;
  interactiveEnabled?: boolean; onReviewDraft?: () => void; onPlay?: () => void;
  onMakeChange?: () => void; iteration?: ComposedRevisionViewV1 | null;
  onCancel?: () => void; cancelling?: boolean;
  filmRun?: CreationFilmRunV1 | null; filmMaterials?: CreationFilmMaterialV1[];
  creationFilmsEnabled?: boolean;
  observedTypicalBuildDurationMs?: number;
  onDraft: (value: string) => void; onSaveDraft: () => void;
  onSelect: (id: string) => void; onApply: () => void; onApplyCover?: () => void; coverUrl?: string;
}

/** Keep immutable source-version markers for audit, while still showing a
 * reused Repair job through the latest version's explicit job references. */
export function relevantCreationRunJobs(
  jobs: Array<AssetGenJob & { id: string }>,
  originVersion: number,
  latest: Pick<CreationVersion, 'creationRun'> | null,
): Array<AssetGenJob & { id: string }> {
  const referenced = new Set([
    latest?.creationRun?.visualJobId,
    latest?.creationRun?.musicJobId,
  ].filter((id): id is string => !!id));
  return jobs.filter(job => referenced.has(job.id)
    || job.completion?.originVersion === originVersion
    || job.creationRun?.version === originVersion);
}

/** A notice the creator has read: closes it for this page. Big enough to tap. */
function DismissNotice({ onDismiss }: { onDismiss: () => void }) {
  return <button type="button" className="creation-show__dismiss" aria-label="Dismiss this notice" onClick={onDismiss} data-testid="workshop-notice-dismiss">×</button>;
}

/** Build adapter for the same stage used during discovery and planning. */
export function WorkshopView(p: WorkshopViewProps) {
  const running = !!p.view && !p.view.terminal;
  const playable = p.active?.buildStatus === 'live';
  const ready = p.version?.buildStatus === 'live' && (p.version.n == null ? playable : p.active?.n === p.version.n);
  const candidates = p.variants;
  const currentJobs = new Set(p.jobs.map(job => job.id));
  // Code carries a saved choice when a revision goes live, so only this
  // origin's choices are live ones. A choice on another origin is waiting: a
  // running revision has not taken it yet, or it was saved on a run that failed.
  const selections = p.state.selections.filter(item => item.originVersion === p.originVersion);
  const waiting = p.state.selections.filter(item => item.originVersion !== p.originVersion);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  const dismiss = (key: string) => setDismissed(old => new Set(old).add(key));
  const notices = (p.state.carryNotices ?? []).filter(item => item.version === p.originVersion && !dismissed.has(`carry:${item.slot}`));
  const waitingShown = waiting.filter(item => !dismissed.has(`waiting:${item.originVersion}:${item.slot}`));
  const slotLabel = (slot: string) => slot.replace(/^[^:]+:/, '').replaceAll('-', ' ');
  const unapplied = (item: WorkshopSelection) => workshopSelectionStatus(item, p.active, p.attachedAssets, running) !== 'applied';
  const groups = new Map<string, AssetVariant[]>();
  for (const v of candidates) if (v.reviewState !== 'superseded' && v.reviewState !== 'rejected' && v.name !== 'title-overlay') groups.set(workshopSlot(v), [...groups.get(workshopSlot(v)) ?? [], v]);
  const timing = p.version ? journeyTiming(p.version, p.history ?? [], p.now, p.observedTypicalBuildDurationMs) : null;
  const categoryFor = (variant: AssetVariant): ShowArtifact['category'] => {
    const hint = `${variant.kind} ${variant.name}`.toLowerCase();
    if (hint.includes('cover') || hint.includes('title') || hint.includes('logo')) return 'identity';
    if (variant.kind === 'music' || variant.kind === 'sfx') return 'sound';
    if (hint.includes('background') || hint.includes('world') || hint.includes('scene') || hint.includes('environment')) return 'world';
    if (variant.strip || hint.includes('hero') || hint.includes('player') || hint.includes('enemy') || hint.includes('character') || hint.includes('sprite')) return 'characters';
    return 'extras';
  };
  const selectedIds = new Set(selections.map(item => item.variantId));
  const artifacts: ShowArtifact[] = [...(p.discoveries ?? []), ...candidates.map((v): ShowArtifact => ({
    id: v.id, title: v.name.replaceAll('-', ' '), summary: v.strip ? `${v.strip.entityName} · ${v.strip.state}` : '',
    kind: v.kind === 'music' || v.kind === 'sfx' ? 'audio' : v.strip ? 'animation' : 'image',
    group: workshopSlot(v), url: v.reviewState === 'rejected' ? undefined : v.url, strip: v.strip, unavailable: v.reviewState === 'rejected',
    category: categoryFor(v), version: p.showVersion, gameAsset: true,
    origin: currentJobs.has(v.genJobId ?? '') ? 'current-run' : 'existing',
    state: v.reviewState === 'rejected' ? 'unavailable' : p.active?.assetsSnapshot?.some(asset => asset.variantId === v.id) ? 'current'
      : selectedIds.has(v.id) ? 'selected' : v.reviewState === 'superseded' ? 'unused' : 'recommended',
    label: v.reviewState === 'rejected' ? 'Not used' : p.active?.assetsSnapshot?.some(asset => asset.variantId === v.id) ? 'In your game'
      : v.reviewState === 'superseded' ? 'Earlier alternative' : currentJobs.has(v.genJobId ?? '') ? 'New candidate' : 'From an earlier version',
  }))];
  for (const variant of candidates) if (variant.rawPreviewUrl && variant.reviewState !== 'rejected') artifacts.push({
    id: `raw:${variant.id}`, title: `${variant.name.replaceAll('-', ' ')} taking shape`, summary: '',
    kind: 'image', url: variant.rawPreviewUrl, group: workshopSlot(variant), category: categoryFor(variant),
    origin: 'current-run', state: 'recommended', label: 'First look', version: p.showVersion,
  });
  if (p.coverUrl && !artifacts.some(item => item.url === p.coverUrl)) artifacts.unshift({
    id: `cover:${p.creationId}`, title: `${p.title} cover`, summary: 'The current cover for your game.', kind: 'image',
    url: p.coverUrl, label: 'Current game identity', category: 'identity', gameAsset: true, origin: 'existing', state: 'current', version: p.active?.n,
  });
  for (const capture of p.state.showCaptures ?? []) artifacts.push({
    id: capture.id, title: capture.title, summary: 'Captured while your game was being tested.',
    kind: 'capture', url: capture.url, label: `Playable proof · version ${capture.version}`,
    category: 'proof', beat: 'playable-proof', gameAsset: true, origin: capture.version === p.showVersion ? 'current-run' : 'existing',
    state: capture.version === p.active?.n ? 'current' : 'recommended', version: capture.version,
  });
  // The exact active version subscription is already realtime. When its
  // bounded recorded preview arrives, expose that sealed replay as film
  // material and in Collection without inventing another browser endpoint.
  if (p.active?.previewUrl && !artifacts.some(item => item.url === p.active?.previewUrl)) artifacts.push({
    id: `sealed-replay:${p.creationId}:v${p.active.n}`,
    title: `${p.title} gameplay replay`,
    summary: `A sealed replay recorded from playable version ${p.active.n}.`,
    kind: 'video', url: p.active.previewUrl, posterUrl: p.active.posterUrl ?? undefined,
    label: `Sealed gameplay replay · version ${p.active.n}`, category: 'proof', beat: 'playable-proof', gameAsset: true,
    origin: 'existing', state: 'current', version: p.active.n,
  });
  appendFilmMaterialArtifacts(artifacts, p.filmMaterials ?? []);
  const decision = p.interactiveEnabled === false ? undefined : [...groups.entries()]
    .map(([slot, items]) => [slot, currentJobs.size ? items.filter(item => currentJobs.has(item.genJobId ?? '')) : items] as const)
    .find(([slot, items]) => items.length > 1 && !p.state.selections.some(item => item.slot === slot));
  const emptyState = mediaEmptyState(p.jobs, p.view?.phase ?? 'queued', ready);
  const activity = ready ? 'Your game is ready' : p.view?.failed ? 'This version needs a repair'
    : p.iteration?.status === 'accepted' ? `Building your change: ${p.iteration.request?.split('\n')[0] ?? 'saved update'}`
    : p.view?.phase === 'queued' ? 'Waiting for a builder' : p.view ? CREATION_GUIDES[p.view.phase].title : 'Preparing your game';
  const choices = (items: AssetVariant[]) => items.map(v => <button type="button" key={v.id} disabled={p.busy}
    onClick={() => p.onSelect(v.id)} aria-pressed={selections.some(item => item.variantId === v.id)}>
      {v.kind !== 'music' && v.kind !== 'sfx' && <img src={v.url} alt="" style={{ width: 80, height: 60, objectFit: 'contain', display: 'block' }} />}
      Choose {v.name.replaceAll('-', ' ')} · {items.indexOf(v) + 1}</button>);
  const canApplyArtwork = playable && !running && selections.some(item => !item.slot.startsWith('cover:') && unapplied(item));
  const canApplyCover = selections.some(item => item.slot.startsWith('cover:') && unapplied(item));
  const choiceTarget = decision?.[0].startsWith('cover:') ? 'the current game cover' : `your saved artwork for version ${p.originVersion}, applied when you choose`;
  return <CreationShow onEvent={p.onEvent} identity={`${p.creationId}:v${p.showVersion}`} title={p.title}
    chapter={buildShowChapter(p.view?.phase, ready)} activity={activity} running={running} ready={ready} failed={p.view?.failed}
    notInYetLine={p.version?.creationRun?.notInYet?.length ? composedNotInYetLineV1(p.version.creationRun.notInYet) : undefined}
    lastUpdatedAt={p.view?.since ?? undefined} artifacts={artifacts} secondary={emptyState}
    playUrl={playable ? playUrl(p.creationId) : undefined} onPlay={p.onPlay}
    filmEnabled={p.creationFilmsEnabled !== false} filmRunSeed={p.filmRun ? String(p.filmRun.seed) : undefined}
    onCancel={p.onCancel} cancelling={p.cancelling}
    choiceKey={decision?.[0]} choiceVisible={!!decision} choiceAffects={choiceTarget}
    choice={decision ? <><h3>Which {decision[1][0]!.name.replaceAll('-', ' ')} feels right?</h3><p>Your choice is saved immediately. The game keeps building.</p><div className="creation-show__choice-grid">{choices(decision[1])}</div></> : undefined}
    actions={notices.length || waitingShown.length || canApplyArtwork || canApplyCover || p.error || (playable && p.onMakeChange) ? <>{notices.map(item => <p role="status" data-testid="workshop-carry-notice" key={item.slot}>Your saved choice for {slotLabel(item.slot)} was not kept on version {item.version}: {item.reason === 'slot-gone' ? 'this version no longer has that slot' : item.reason === 'variant-gone' ? 'that artwork is no longer available' : 'the character it animates changed'}.<DismissNotice onDismiss={() => dismiss(`carry:${item.slot}`)} /></p>)}
      {waitingShown.map(item => <p data-testid="workshop-waiting-choice" key={`${item.originVersion}:${item.slot}`}>{slotLabel(item.slot)}: saved on version {item.originVersion}. It moves to the next version that goes live, if its slot still exists.<DismissNotice onDismiss={() => dismiss(`waiting:${item.originVersion}:${item.slot}`)} /></p>)}
      {canApplyArtwork && <button type="button" disabled={p.busy} onClick={p.onApply}>Apply saved artwork</button>}
      {canApplyCover && <button type="button" disabled={p.busy} onClick={p.onApplyCover}>Apply cover</button>}
      {p.error && <p role="alert">{p.error}</p>}{playable && p.onMakeChange && <button type="button" className="btn" onClick={p.onMakeChange}>Make a change</button>}</> : undefined}
    details={<>
      <p>{emptyState}</p>
      {timing?.overrun && <p>This run is taking longer than similar builds. The status above shows the latest saved activity.</p>}
      {p.version?.creationRun?.musicDefaultReason && <p data-testid="music-default-reason">A soundtrack default was selected: {p.version.creationRun.musicDefaultReason}.</p>}
      {p.view && <BuildTracker view={p.view} now={p.now} />}
      {p.interactiveEnabled !== false && <section aria-label="Artwork choices"><h3>Choose artwork and sound</h3>
        <p>Selections are saved. Applying them publishes an artwork update and does not restart your build. When a game update goes live, they move to the new version if their slot still exists.</p>
        {[...groups.entries()].map(([slot, items]) => <div key={slot}>
          <h4>{items[0]!.name.replaceAll('-', ' ')}</h4>{choices(items)}
          {selections.filter(item => item.slot === slot).map(item => <p key={item.variantId}>{workshopSelectionStatus(item, p.active, p.attachedAssets, running) === 'applied' ? 'Applied to your game' : 'Selected · not applied yet'}</p>)}
        </div>)}
      </section>}
    </>} />;
}

export function DeejWorkshop({ creation, latest, active, view, now, history = [], onReviewDraft, onMakeChange, iteration, onCancel, cancelling, creationFilmsEnabled = true }: {
  creation: Creation & { id: string }; latest: (CreationVersion & { id: string }) | null;
  active: (CreationVersion & { id: string }) | null; view: BuildStatusView | null; now: number;
  history?: Array<CreationVersion & { id: string }>; onReviewDraft?: (draft: string) => void;
  onMakeChange?: () => void; iteration?: ComposedRevisionViewV1 | null; onCancel?: () => void; cancelling?: boolean;
  creationFilmsEnabled?: boolean;
}) {
  const services = useServices();
  const { runAttributed, attributionModal } = useAttributedAction();
  const dirtyDraft = useRef(false);
  const observedRevision = useRef(0);
  const [serverOrigin, setServerOrigin] = useState<number>();
  const [serverShowVersion, setServerShowVersion] = useState<number>();
  const [interactiveEnabled, setInteractiveEnabled] = useState(true);
  const [observedTypicalBuildDurationMs, setObservedTypicalBuildDurationMs] = useState<number>();
  const [state, setState] = useState(emptyWorkshop);
  const [variants, setVariants] = useState<AssetVariant[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [discoveries, setDiscoveries] = useState<ShowArtifact[]>([]);
  useEffect(() => {
    let cancelled = false;
    void services.callable.getConceptAnalysisResume({ creationId: creation.id }).then(result => {
      if (cancelled) return;
      const draft = result.analysisDraft;
      const proposal = draft?.result && 'proposal' in draft.result ? draft.result.proposal : undefined;
      const items = analysisDiscoveries({ concept: creation.concept ?? creation.title, referenceUrls: draft?.inputs.referenceUrls ?? [],
        design: creation.gameDesign ?? proposal?.design, referenceBriefs: draft?.referenceBriefs, videoBriefs: draft?.videoBriefs });
      const saved = (draft?.progress?.completedArtifacts ?? []).filter(item => item.kind !== 'gameplay-idea' && item.kind !== 'vocabulary');
      setDiscoveries([...new Map([...items, ...saved].map(item => [item.id, item])).values()].map(item => ({ id: item.id, title: item.title, summary: item.summary, kind: item.thumbnailUrl ? 'image' : 'discovery', url: item.thumbnailUrl, label: 'Your game direction' })));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [creation.id, creation.gameDesign, services]);
  useEffect(() => {
    const refresh = () => setRefreshKey(key => key + 1);
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    const timer = view && !view.terminal ? window.setInterval(() => { if (!document.hidden) refresh(); }, 10_000) : undefined;
    // Gameplay captures are deliberately published outside the playable
    // barrier. Re-read twice after a terminal transition so a late exact-job
    // upload appears without requiring a reload or visibility change.
    const terminalRefreshes = view?.terminal
      ? [window.setTimeout(refresh, 1_500), window.setTimeout(refresh, 8_000)]
      : [];
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', visible);
    return () => {
      clearInterval(timer);
      terminalRefreshes.forEach(clearTimeout);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [view?.terminal]);
  const originVersion = serverOrigin ?? latest?.artCompletionOf ?? latest?.n ?? creation.latestVersion;
  const showVersion = serverShowVersion ?? originVersion;
  const { run: filmRun, materials: filmMaterials } = useCreationFilm(creation.id, creationFilmsEnabled, {
    kind: 'game',
    manifestRunId: latest?.creationFilmManifest?.runId ?? active?.creationFilmManifest?.runId,
    versions: [originVersion, latest?.n, active?.n].filter((value): value is number => value != null),
  });
  const jobs = useSubscription<Array<AssetGenJob & { id: string }>>(cb => services.subscribe.assetGenJobs(creation.id, cb), [], [creation.id]);
  const relevantJobs = useMemo(
    () => relevantCreationRunJobs(jobs, showVersion, latest),
    [jobs, showVersion, latest?.creationRun?.visualJobId, latest?.creationRun?.musicJobId],
  );
  const jobKey = jobs.map(j => `${j.id}:${j.status}`).join('|');
  useEffect(() => {
    let cancelled = false;
    const retryOnce = async <T,>(load: () => Promise<T>): Promise<T> => {
      try { return await load(); } catch { return load(); }
    };
    Promise.allSettled([
      retryOnce(() => services.callable.workshop({ creationId: creation.id, action: 'read' })),
      retryOnce(() => services.fetchAssetVariants(creation.id)),
    ]).then(([workshopResult, assetResult]) => {
      if (cancelled) return;
      if (assetResult.status === 'fulfilled') setVariants([...assetResult.value].reverse());
      if (workshopResult.status === 'rejected') {
        setRefreshError('Artwork choices couldn’t be refreshed. Your build continues; trying again shortly.');
        return;
      }
      const result = workshopResult.value;
      setRefreshError(assetResult.status === 'fulfilled' ? '' : 'Artwork choices couldn’t be refreshed. Your build continues; trying again shortly.');
      if (result.state.revision < observedRevision.current) return;
      observedRevision.current = result.state.revision;
      setState(result.state);
      setServerOrigin(result.originVersion); setServerShowVersion(result.showVersion); setInteractiveEnabled(result.interactiveEnabled !== false);
      setObservedTypicalBuildDurationMs(result.typicalBuildDurationMs);
      if (!dirtyDraft.current) setDraft(result.state.drafts[String(result.originVersion ?? originVersion)] ?? '');
    });
    return () => { cancelled = true; };
  }, [creation.id, jobKey, latest?.n, latest?.buildStatus, refreshKey, services]);
  useEffect(() => { dirtyDraft.current = false; setDraft(state.drafts[String(originVersion)] ?? ''); }, [creation.id, originVersion]);
  async function save(action: 'select' | 'draft' | 'apply' | 'cover', extra: Partial<Parameters<Callables['workshop']>[0]> = {}) {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await runAttributed(creation, async attributeTo => {
        const result = await services.callable.workshop({ creationId: creation.id, action, originVersion, expectedRevision: state.revision, ...extra, ...(attributeTo ? { attributeTo } : {}) });
        if (result.state.revision < observedRevision.current) return;
        observedRevision.current = result.state.revision;
        setState(result.state);
        if (action === 'draft' && !result.stale) dirtyDraft.current = false;
        if (result.stale) setError('A collaborator saved a newer choice. We’ve restored it here; review it before choosing again.');
      });
    } catch (e) { setError(e instanceof Error ? e.message : 'Your choice could not be saved. Try again.'); }
    finally { setBusy(false); }
  }
  return <>{attributionModal}<WorkshopView title={creation.title} creationId={creation.id} originVersion={originVersion} showVersion={showVersion} attachedAssets={creation.assets ?? []} view={view} active={active?.n === creation.activeVersion ? active : null} now={now} version={latest} history={history}
    filmRun={filmRun} filmMaterials={filmMaterials} creationFilmsEnabled={creationFilmsEnabled}
    discoveries={discoveries} onEvent={event => { void services.callable.workshop({ creationId: creation.id, action: 'event', event, originVersion: showVersion }).catch(() => {}); }} jobs={relevantJobs} variants={variants} state={state} draft={draft} suggestion={creation.suggestedIteratePrompt} busy={busy} error={error || refreshError} observedTypicalBuildDurationMs={observedTypicalBuildDurationMs}
    interactiveEnabled={interactiveEnabled} onReviewDraft={() => onReviewDraft?.(draft)} onCancel={onCancel} cancelling={cancelling} onMakeChange={onMakeChange} iteration={iteration} onPlay={() => { void services.callable.workshop({ creationId: creation.id, action: 'play' }).catch(() => {}); }}
    onDraft={value => { dirtyDraft.current = true; setDraft(value); }} onSaveDraft={() => void save('draft', { draft })} onSelect={variantId => void save('select', { variantId })} onApply={() => void save('apply')} onApplyCover={() => void save('cover')} coverUrl={creation.coverUrl} /></>;
}
